/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import debounce from 'lodash.debounce';
import { Search } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useToast } from '../ui/use-toast';

import { IWithPagination, UserDataWithRole } from '@/commons/commons';
import { MonitoredOrg, Organisation } from '@/commons/protocols';
import Pagination from '@/components/list/Pagination';
import { useGet, useMutate } from '@/net/commons';

interface OperatorOrganisationsDialogProps {
    operator: UserDataWithRole | null;
    onClose: () => void;
}

export default function OperatorOrganisationsDialog({ operator, onClose }: OperatorOrganisationsDialogProps) {
    const [selectedOrganisations, setSelectedOrganisations] = useState<MonitoredOrg[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInputValue, setSearchInputValue] = useState('');
    const perPage = 10;
    const { toast } = useToast();
    const { data: monitoredOrgsData, isLoading: isLoadingMonitoredOrgs } = useGet<{ monitoredOrgs: MonitoredOrg[] }>(
        operator ? `/bff/organisation/members/${operator.id}/operator-organisations` : null,
    );
    const { data: organisations, isLoading } = useGet<IWithPagination<Organisation>>(
        operator
            ? `/bff/organisations?page=${currentPage}&pageSize=${perPage}${searchQuery ? `&name=${searchQuery}` : ''}`
            : null,
    );
    const { trigger: updateOperatorOrganisations } = useMutate('/bff/organisation/members/:id/operator-organisations', {
        method: 'PATCH',
    });

    const shouldShowPagination = (organisations?.pagination?.total || 0) > perPage;

    useEffect(() => {
        if (monitoredOrgsData) {
            setSelectedOrganisations(monitoredOrgsData.monitoredOrgs ?? []);
        }
    }, [monitoredOrgsData]);

    const handleOrganisationToggle = (org: Organisation) => {
        setSelectedOrganisations((prev) =>
            prev.some((selected) => selected.id === org.id)
                ? prev.filter((selected) => selected.id !== org.id)
                : [...prev, org],
        );
    };

    const handleSaveOperatorOrganisations = async () => {
        if (!operator) return;
        try {
            await updateOperatorOrganisations({
                idParam: operator.id,
                organisations: selectedOrganisations,
            });
            toast({
                title: 'Success',
                description: 'Organisations updated successfully',
            });
        } catch (error) {
            toast({
                title: 'Failed to update organisations',
                description: 'Please try again',
                variant: 'destructive',
            });
        }
    };

    const debouncedSetSearchQuery = useCallback(
        debounce((value: string) => {
            setSearchQuery(value);
            setCurrentPage(1);
        }, 300),
        [],
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInputValue(value);
        debouncedSetSearchQuery(value);
    };

    return (
        <Dialog open={!!operator} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Manage Operator Organisations</DialogTitle>
                    <DialogDescription>Select the organisations that {operator?.email} will monitor</DialogDescription>
                </DialogHeader>
                <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        type="search"
                        placeholder="Search by name..."
                        className="pl-8"
                        value={searchInputValue}
                        onChange={handleSearchChange}
                    />
                </div>
                <div className="w-full overflow-auto rounded-md border">
                    <table className="w-full caption-bottom text-sm">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[50px]">Select</TableHead>
                                <TableHead>Organisation Name</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading || isLoadingMonitoredOrgs ? (
                                <TableRow>
                                    <TableCell colSpan={2} className="text-center text-muted-foreground">
                                        Loading organisations...
                                    </TableCell>
                                </TableRow>
                            ) : organisations?.data.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={2} className="text-center text-muted-foreground">
                                        {searchQuery ? 'No organisations found' : 'No organisations available'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                organisations?.data.map((org) => (
                                    <TableRow key={org.id}>
                                        <TableCell>
                                            <input
                                                type="checkbox"
                                                checked={selectedOrganisations.some(
                                                    (selected) => selected.id === org.id,
                                                )}
                                                onChange={() => handleOrganisationToggle(org)}
                                                // eslint-disable-next-line max-len
                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                        </TableCell>
                                        <TableCell>{org.name}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </table>
                </div>
                {organisations?.pagination && shouldShowPagination && (
                    <Pagination {...organisations.pagination} onPageChange={setCurrentPage} />
                )}
                <div className="flex justify-end mt-4">
                    <Button onClick={handleSaveOperatorOrganisations}>Save Changes</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
